/**
 * Step 4: Gazebo Configuration
 * Physics settings and plugins
 */

import {
  Box,
  Title,
  Text,
  Stack,
  Paper,
  NumberInput,
  Select,
  Switch,
  Group,
  Code,
} from "@mantine/core";
import { useConfigureStore } from "../ConfigureStore";

export function GazeboStep() {
  const {
    gazeboPhysics,
    gazeboPlugins,
    updateGazeboPhysics,
    toggleGazeboPlugin,
    updateGazeboPlugin,
  } = useConfigureStore();

  const solverOptions = [
    { value: "ode", label: "ODE (Open Dynamics Engine)" },
    { value: "bullet", label: "Bullet Physics" },
    { value: "simbody", label: "Simbody" },
    { value: "dart", label: "DART" },
  ];

  return (
    <Stack gap="lg">
      <Box>
        <Title order={3} mb="xs">
          Gazebo Configuration
        </Title>
        <Text c="dimmed" size="sm">
          Configure physics simulation and plugins for Gazebo.
        </Text>
      </Box>

      {/* Physics Settings */}
      <Paper withBorder p="md">
        <Title order={5} mb="md">
          Physics Settings
        </Title>
        <Stack gap="md">
          <Select
            label="Physics Solver"
            description="The physics engine used for simulation"
            data={solverOptions}
            value={gazeboPhysics.solver}
            onChange={(value) =>
              updateGazeboPhysics({
                solver: (value as typeof gazeboPhysics.solver) || "ode",
              })
            }
          />

          <NumberInput
            label="Step Size (seconds)"
            description="Simulation time step. Smaller values = more accurate but slower"
            value={gazeboPhysics.stepSize}
            onChange={(value) =>
              updateGazeboPhysics({
                stepSize: typeof value === "number" ? value : 0.001,
              })
            }
            min={0.0001}
            max={0.1}
            step={0.001}
            decimalScale={4}
          />

          <NumberInput
            label="Real Time Factor"
            description="Target simulation speed relative to real time (1.0 = real-time)"
            value={gazeboPhysics.realTimeFactor}
            onChange={(value) =>
              updateGazeboPhysics({
                realTimeFactor: typeof value === "number" ? value : 1.0,
              })
            }
            min={0.1}
            max={10}
            step={0.1}
            decimalScale={1}
          />
        </Stack>
      </Paper>

      {/* Plugins */}
      <Paper withBorder p="md">
        <Title order={5} mb="md">
          Gazebo Plugins
        </Title>
        <Stack gap="md">
          {gazeboPlugins.map((plugin) => (
            <Paper key={plugin.name} withBorder p="sm" bg="gray.0">
              <Group justify="space-between" align="flex-start">
                <Stack gap="xs">
                  <Group gap="xs">
                    <Switch
                      checked={plugin.enabled}
                      onChange={() => toggleGazeboPlugin(plugin.name)}
                      size="sm"
                    />
                    <Text size="sm" fw={500}>
                      {plugin.name}
                    </Text>
                  </Group>
                  <Code>{plugin.filename}</Code>

                  {/* Plugin-specific parameters */}
                  {plugin.name === "joint_state_publisher" && plugin.enabled && (
                    <NumberInput
                      label="Update Rate (Hz)"
                      size="xs"
                      value={
                        typeof plugin.parameters.update_rate === "number"
                          ? plugin.parameters.update_rate
                          : 100
                      }
                      onChange={(value) =>
                        updateGazeboPlugin(plugin.name, {
                          update_rate: typeof value === "number" ? value : 100,
                        })
                      }
                      min={1}
                      max={1000}
                      step={10}
                      style={{ maxWidth: 150 }}
                    />
                  )}
                </Stack>
              </Group>
            </Paper>
          ))}
        </Stack>
      </Paper>

      {/* Preview */}
      <Paper withBorder p="md" bg="gray.0">
        <Title order={5} mb="sm">
          Configuration Preview
        </Title>
        <Code block style={{ whiteSpace: "pre-wrap" }}>
          {`<physics name="default_physics" type="${gazeboPhysics.solver}">
  <max_step_size>${gazeboPhysics.stepSize}</max_step_size>
  <real_time_factor>${gazeboPhysics.realTimeFactor}</real_time_factor>
  <real_time_update_rate>${Math.round(1 / gazeboPhysics.stepSize)}</real_time_update_rate>
</physics>

${gazeboPlugins
  .filter((p) => p.enabled)
  .map(
    (p) => `<plugin filename="${p.filename}" name="${p.name}">
  ${Object.entries(p.parameters)
    .map(([k, v]) => `<${k}>${v}</${k}>`)
    .join("\n  ")}
</plugin>`
  )
  .join("\n\n")}`}
        </Code>
      </Paper>
    </Stack>
  );
}
